import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import MobileStepper from '@material-ui/core/MobileStepper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import DialogueBase from '../../../components/dialogues/DialogueBase';
import DocumentsImg from '../../../assets/account-feature-documents.png';
import ServicesImg from '../../../assets/account-feature-services.png';
import NewsImg from '../../../assets/account-feature-news.png';
import FeedbackImg from '../../../assets/account-feature-feedback.png';
import StartImg from '../../../assets/account-feature-start.png';

const steps = [
  {
    id: 1,
    title: 'Welcome to your new Clickability participant account!',
    content: 'Let\'s quickly go through the key features so you can start simplifying your NDIS journey. Click NEXT on the bottom right of this screen to proceed',
    img: StartImg,
  },
  {
    id: 2,
    title: '1. Store, categorise and search your NDIS and provider documents.',
    content: 'No more searching your desktop for those invoices or service agreements you got a few months ago. Store your documents on Clickability and you can easily find them whenever you need. Also, use your unique vault address to save documents attached to emails!',
    img: DocumentsImg,
  },
  {
    id: 3,
    title: '2. View and manage your provider service requests.',
    content: 'If you request services from providers through Clickability, the details of your request will appear here. This allows you to see where things are at any time. Soon you\'ll also be able to respond to providers here.',
    img: ServicesImg,
  },
  {
    id: 4,
    title: '3. Keep up to date with news and views from media and the web.',
    content: 'Here you can find a feed of the latest news from the online disability community. We\'ll be adding more content in the near future, including social and blog feeds, so let us know what you\'d like to see here.',
    img: NewsImg,
  },
  {
    id: 5,
    title: '4. Tell us what will help you achieve your goals faster.',
    content: 'We want to know what tools would really help you achieve your NDIS goals. We\'ve only just started and plan to add a lot more to your Clickability account in the months ahead!',
    img: FeedbackImg,
  },
];

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  container: {
    width: '45rem',
    marginBottom: '3rem',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  content: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  img: {
    margin: '1rem 0',
    overflow: 'hidden',
    display: 'block',
    width: '100%',
  },
}));

const IntroDialogue = ({open, onClose}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [activeStep, setActiveStep] = React.useState(0);
  const maxSteps = steps.length;

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const renderNextOrFinish = (handleNext, handleClose) => {
    if (activeStep === maxSteps - 1) {
      return (
        <Button size="large" onClick={handleClose}>
          Finish
        </Button>
      );
    } else {
      return (
        <Button size="large" onClick={handleNext}>
          Next
          {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
        </Button>
      );
    }
  };

  return (
    <DialogueBase open={open} onClose={onClose} title={null}>
      <div className={classes.root}>
        <div className={classes.container}>
          <div className={classes.content}>
            <Typography variant="h1" align="center" color="primary" gutterBottom>{steps[activeStep].title}</Typography>
            <Typography variant="h3" align="center" gutterBottom>{steps[activeStep].content}</Typography>
            <img
              className={classes.img}
              src={steps[activeStep].img}
              alt={steps[activeStep].title}
            />
          </div>
          <MobileStepper
            steps={maxSteps}
            variant="dots"
            position="static"
            activeStep={activeStep}
            nextButton={renderNextOrFinish(handleNext, onClose)}
            backButton={
              <Button size="large" onClick={handleBack} disabled={activeStep === 0}>
                {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
                Back
              </Button>
            }
          />
        </div>
      </div>
    </DialogueBase>
  );
};

IntroDialogue.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default IntroDialogue;
